/**
 * Derivaciones puras de los visuales de habilidades (Sprint 314 D; REQ-MINT-006).
 *
 * - **Matriz modelos × ámbitos**: percentil por celda con «sin dato» explícito.
 * - **Ranking por ámbito**: orden por percentil con marcas P75/P25 (fortaleza/
 *   carencia), «top N % del conjunto» y fecha del dato más antiguo.
 * - **Radar comparativo multi-modelo** (2–4): series superpuestas; un ámbito
 *   sin dato queda fuera del trazo (nunca imputado).
 *
 * Sin datos no se estima nada: las celdas sin valor quedan `null`.
 */

import type { DomainAggregate, OfferingIntelligence } from '../types';

export interface SkillCell {
  domain: string;
  score: number | null;
  benchmarksCount: number | null;
  oldestPublishedAt: string | null;
}

export interface SkillRow {
  key: string;
  label: string;
  provider: string;
  source: string | null;
  cells: SkillCell[];
}

/** Ámbitos presentes en el conjunto, en orden de aparición. */
export function skillDomains(eligible: OfferingIntelligence[]): string[] {
  const seen: string[] = [];
  for (const item of eligible) {
    for (const domain of item.benchmarks?.domains ?? []) {
      if (!seen.includes(domain.domain)) seen.push(domain.domain);
    }
  }
  return seen;
}

/** Matriz modelos × ámbitos (una fila por elegible; celda `null` = sin dato). */
export function skillMatrix(eligible: OfferingIntelligence[]): SkillRow[] {
  const domains = skillDomains(eligible);
  return eligible.map((item) => {
    const byDomain = new Map<string, DomainAggregate>(
      (item.benchmarks?.domains ?? []).map((domain) => [domain.domain, domain]),
    );
    return {
      key: item.offering.sheet_id,
      label: item.offering.model_ref ?? item.offering.provider_model_id,
      provider: item.offering.provider,
      source: item.benchmarks?.source ?? null,
      cells: domains.map((domain) => {
        const found = byDomain.get(domain);
        return {
          domain,
          score: found ? found.score : null,
          benchmarksCount: found ? found.benchmarks_count : null,
          oldestPublishedAt: found?.oldest_published_at ?? null,
        };
      }),
    };
  });
}

/**
 * Ordena las filas por el percentil del ámbito indicado (o por la media de sus
 * ámbitos si no se indica); las celdas sin dato van al final.
 */
export function sortSkillRows(rows: SkillRow[], domain?: string): SkillRow[] {
  const valueOf = (row: SkillRow): number | null => {
    if (domain) {
      return row.cells.find((cell) => cell.domain === domain)?.score ?? null;
    }
    const scores = row.cells.map((cell) => cell.score).filter((score): score is number => score !== null);
    if (!scores.length) return null;
    return scores.reduce((acc, score) => acc + score, 0) / scores.length;
  };
  return [...rows].sort((a, b) => {
    const aValue = valueOf(a);
    const bValue = valueOf(b);
    if (aValue === null && bValue === null) return a.label.localeCompare(b.label);
    if (aValue === null) return 1;
    if (bValue === null) return -1;
    return bValue - aValue || a.label.localeCompare(b.label);
  });
}

export interface RankingEntry {
  key: string;
  label: string;
  provider: string;
  score: number;
  topPct: number;
  strength: boolean;
  gap: boolean;
}

export interface DomainRanking {
  domain: string;
  entries: RankingEntry[];
  p75: number | null;
  p25: number | null;
  oldestPublishedAt: string | null;
}

/** Cuantil por interpolación lineal sobre una lista ya ordenada. */
function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return 0;
  const position = (sorted.length - 1) * q;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

/**
 * Ranking de un ámbito con umbrales P75/P25 (fortaleza/carencia) y «top N %».
 * Solo entran modelos con dato del ámbito; el conjunto es el de la comparación.
 */
export function domainRanking(eligible: OfferingIntelligence[], domain: string): DomainRanking {
  const values: Array<{
    key: string;
    label: string;
    provider: string;
    score: number;
    oldest: string | null;
  }> = [];
  for (const item of eligible) {
    const found = (item.benchmarks?.domains ?? []).find((entry) => entry.domain === domain);
    if (!found) continue;
    values.push({
      key: item.offering.sheet_id,
      label: item.offering.model_ref ?? item.offering.provider_model_id,
      provider: item.offering.provider,
      score: found.score,
      oldest: found.oldest_published_at ?? null,
    });
  }
  values.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  const scores = values.map((value) => value.score).sort((a, b) => a - b);
  const p75 = scores.length ? quantile(scores, 0.75) : null;
  const p25 = scores.length ? quantile(scores, 0.25) : null;
  const entries: RankingEntry[] = values.map((value, index) => ({
    key: value.key,
    label: value.label,
    provider: value.provider,
    score: value.score,
    topPct: Math.max(1, Math.round(((index + 1) / values.length) * 100)),
    strength: p75 !== null && value.score >= p75,
    gap: p25 !== null && value.score <= p25,
  }));
  const oldest =
    values
      .map((value) => value.oldest)
      .filter((value): value is string => Boolean(value))
      .sort()[0] ?? null;
  return { domain, entries, p75, p25, oldestPublishedAt: oldest };
}

export interface RadarRow {
  domain: string;
  [key: string]: number | string | null;
}

/** Series del radar multi-modelo (máx. 4; sin dato no se imputa). */
export function radarSeries(
  eligible: OfferingIntelligence[],
  selectedKeys: string[],
  maxModels = 4,
): { rows: RadarRow[]; series: Array<{ key: string; label: string }> } {
  const chosen = eligible
    .filter((item) => selectedKeys.includes(item.offering.sheet_id))
    .slice(0, maxModels);
  const domains = skillDomains(chosen);
  const series = chosen.map((item) => ({
    key: item.offering.sheet_id,
    label: item.offering.model_ref ?? item.offering.provider_model_id,
  }));
  const rows: RadarRow[] = domains.map((domain) => {
    const row: RadarRow = { domain };
    for (const item of chosen) {
      const found = (item.benchmarks?.domains ?? []).find((entry) => entry.domain === domain);
      row[item.offering.sheet_id] = found ? found.score : null;
    }
    return row;
  });
  return { rows, series };
}

/** Selección inicial del radar: los dos primeros elegibles con datos de ámbito. */
export function defaultRadarSelection(eligible: OfferingIntelligence[], maxModels = 4): string[] {
  return eligible
    .filter((item) => (item.benchmarks?.domains?.length ?? 0) > 0)
    .slice(0, Math.min(2, maxModels))
    .map((item) => item.offering.sheet_id);
}
